import { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavbar } from "../navbar.context";
import { isTouchscreen } from "../../../utils/device";
import { useRovingFocus, useKeypress, useOutsideClick, useWindowResize } from "../../../hooks";

interface UseNavbarMenuProps {
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  contentRef: React.RefObject<HTMLUListElement | null>;
  portalRef?: React.RefObject<HTMLDivElement | null>;
  id: string;
  onPointerLeave?: (event: React.PointerEvent<HTMLLIElement>) => void;
}

export function useNavbarMenu(props: UseNavbarMenuProps) {
  const { triggerRef, contentRef, portalRef, id, onPointerLeave } = props;

  const { value, toggleMenu } = useNavbar();

  const isOpen = useMemo(() => value === id, [value, id]);

  // Track whether the menu was opened via hover to prevent click from immediately closing it
  const openedByHover = useRef(false);

  const getMenuPosition = useCallback(() => {
    if (!triggerRef.current || !portalRef?.current || !contentRef.current) return;

    const offset = triggerRef.current.offsetLeft;
    const height = contentRef.current.scrollHeight;

    return { x: offset, height };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs are stable and don't need to be in deps
  }, [portalRef]);

  const setMenuPosition = useCallback(() => {
    const pos = getMenuPosition();

    if (!portalRef?.current || !pos) return;

    portalRef.current.style.transform = `translateX(${pos.x}px)`;
  }, [getMenuPosition, portalRef]);

  const handleToggleMenu = useCallback(() => {
    if (isOpen && openedByHover.current) {
      // First click after hover-open: confirm the open state, don't close
      openedByHover.current = false;
      return;
    }
    openedByHover.current = false;
    toggleMenu(id);
  }, [toggleMenu, id, isOpen]);

  const openOnPointerEnter = useCallback(() => {
    if (isTouchscreen() || isOpen) return;

    openedByHover.current = true;
    toggleMenu(id);
  }, [toggleMenu, id, isOpen]);

  const closeOnPointerLeave = useCallback(
    (event: React.PointerEvent<HTMLLIElement>) => {
      onPointerLeave?.(event);

      if (!isOpen) return;

      openedByHover.current = false;
      toggleMenu(null);
    },
    [toggleMenu, onPointerLeave, isOpen],
  );

  const closeOnOutsideClick = useCallback(() => {
    openedByHover.current = false;
    toggleMenu(null);
  }, [toggleMenu]);

  const onHorizontalArrowPress = useRovingFocus({ orientation: "horizontal" });

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleToggleMenu();
        return;
      }
      onHorizontalArrowPress(event);
    },
    [onHorizontalArrowPress, handleToggleMenu],
  );

  const moveFocusToMenuIfOpen = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen) return;

      // Only respond if the trigger button itself is focused
      if (document.activeElement !== triggerRef.current) return;

      const firstFocusableElement = contentRef.current?.querySelector("button, a");

      if (firstFocusableElement) {
        event.preventDefault();
        event.stopPropagation();
        (firstFocusableElement as HTMLElement).focus();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs are stable
    [isOpen],
  );

  const handleMenuClose = useCallback(() => {
    if (!isOpen) return;

    // Only respond if focus is within the trigger or dropdown content
    const activeEl = document.activeElement;
    const isFocusInTrigger = triggerRef.current === activeEl;
    const isFocusInContent = contentRef.current?.contains(activeEl);
    const isFocusInPortal = portalRef?.current?.contains(activeEl);

    if (!isFocusInTrigger && !isFocusInContent && !isFocusInPortal) return;

    openedByHover.current = false;
    toggleMenu(null);
    triggerRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs are stable
  }, [toggleMenu, isOpen]);

  // Close menu when focus leaves the navbar scope entirely (e.g. Tab-out)
  useEffect(() => {
    if (!isOpen) return;

    let rafId: number;

    const handleFocusOut = (event: FocusEvent) => {
      const relatedTarget = event.relatedTarget instanceof Node ? event.relatedTarget : null;

      // If focus is moving to null (outside document) or outside the navbar scope, close
      const triggerLi = triggerRef.current?.closest("li");
      const isStillInTrigger = triggerLi?.contains(relatedTarget);
      const isStillInContent = contentRef.current?.contains(relatedTarget);
      const isStillInPortal = portalRef?.current?.contains(relatedTarget);

      if (!isStillInTrigger && !isStillInContent && !isStillInPortal) {
        // Use requestAnimationFrame to let focus settle before closing
        rafId = requestAnimationFrame(() => {
          openedByHover.current = false;
          toggleMenu(null);
        });
      }
    };

    document.addEventListener("focusout", handleFocusOut);
    return () => {
      document.removeEventListener("focusout", handleFocusOut);
      cancelAnimationFrame(rafId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs are stable
  }, [isOpen, toggleMenu]);

  useKeypress("ArrowDown", moveFocusToMenuIfOpen);

  useKeypress("Escape", handleMenuClose);

  useOutsideClick({
    ref: portalRef,
    callback: closeOnOutsideClick,
    active: isOpen,
    excludeRef: triggerRef,
  });

  useWindowResize({
    callback: setMenuPosition,
    active: isOpen,
  });

  return {
    getMenuPosition,
    setMenuPosition,
    handleToggleMenu,
    openOnPointerEnter,
    closeOnPointerLeave,
    closeOnOutsideClick,
    handleKeyDown,
    moveFocusToMenuIfOpen,
    handleMenuClose,
  };
}
